/**
 * Environment configuration using Vite best practices
 * All environment variables must be prefixed with VITE_ to be accessible in the browser
 */

interface EnvironmentConfig {
  // API Configuration
  apiBaseUrl: string;
  apiFunctionKey?: string;
  
  // App Configuration
  appName: string;
  appVersion: string;
  
  // Authorization Configuration
  authorizedAdmins: string[];
  
  // Feature Flags
  enableAnalytics: boolean;
  enableDebug: boolean;
  
  // Runtime Environment
  isDevelopment: boolean;
  isProduction: boolean;
}

/**
 * Get environment variable with type safety and validation
 */
function getEnvVar(key: string, defaultValue?: string, allowEmpty: boolean = false): string {
  const value = import.meta.env[key];
  
  // If value is explicitly undefined (not set), use default or throw error
  if (value === undefined) {
    if (defaultValue !== undefined) {
      return defaultValue;
    }
    throw new Error(`Environment variable ${key} is required but not defined`);
  }
  
  // If empty string is allowed, return it
  if (allowEmpty && value === '') {
    return value;
  }
  
  // Otherwise, fall back to default if value is empty
  return value || defaultValue || '';
}

/**
 * Get boolean environment variable
 */
function getBooleanEnvVar(key: string, defaultValue: boolean = false): boolean {
  const value = import.meta.env[key];
  
  if (value === undefined) {
    return defaultValue;
  }
  
  return value.toLowerCase() === 'true';
}

/**
 * Parse comma-separated admin emails from environment variable
 */
function getAdminEmails(): string[] {
  const adminEmailsString = import.meta.env.VITE_AUTHORIZED_ADMINS;
  
  // If not set, return empty array (no admins authorized)
  if (!adminEmailsString) {
    console.warn('VITE_AUTHORIZED_ADMINS not set - no admin access will be granted');
    return [];
  }
  
  // Split by comma, trim whitespace, filter out empty strings, convert to lowercase
  return adminEmailsString
    .split(',')
    .map((email: string) => email.trim().toLowerCase())
    .filter((email: string) => email.length > 0 && email.includes('@'));
}

/**
 * Environment configuration object
 * This provides a single source of truth for all environment variables
 */
export const env: EnvironmentConfig = {
  // API Configuration
  apiBaseUrl: getEnvVar('VITE_API_BASE_URL', '', true), // Allow empty string for relative URLs in production
  apiFunctionKey: import.meta.env.VITE_API_FUNCTION_KEY,
  
  // App Configuration
  appName: getEnvVar('VITE_APP_NAME', 'Ask Clement'),
  appVersion: getEnvVar('VITE_APP_VERSION', '1.0.0'),
  
  // Authorization Configuration
  authorizedAdmins: getAdminEmails(),
  
  // Feature Flags
  enableAnalytics: getBooleanEnvVar('VITE_ENABLE_ANALYTICS'),
  enableDebug: getBooleanEnvVar('VITE_ENABLE_DEBUG'),
  
  // Runtime Environment
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
};

/**
 * Validate that all required environment variables are set
 */
export function validateEnvironment(): void {
  const requiredVars = [
    'VITE_API_BASE_URL',
  ];
  
  // Variables that can be empty strings (for relative URLs in production)
  const allowEmptyVars = ['VITE_API_BASE_URL'];
  
  const missingVars = requiredVars.filter(varName => {
    const value = import.meta.env[varName];
    // If the variable is undefined (not set at all), it's missing
    if (value === undefined) {
      return true;
    }
    // If empty strings are allowed for this variable, it's not missing
    if (allowEmptyVars.includes(varName) && value === '') {
      return false;
    }
    // Otherwise, check if it's empty after trimming
    return !value || value.trim() === '';
  });
  
  if (missingVars.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missingVars.join(', ')}\n` +
      'Please check your .env files and ensure all required variables are set.'
    );
  }
  
  // Log configuration in development
  if (env.isDevelopment && env.enableDebug) {
    console.group('🔧 Environment Configuration');
    console.log('Environment:', import.meta.env.MODE);
    console.log('API Base URL:', env.apiBaseUrl);
    console.log('App Name:', env.appName);
    console.log('App Version:', env.appVersion);
    console.log('Debug Mode:', env.enableDebug);
    console.log('Analytics:', env.enableAnalytics);
    console.groupEnd();
  }
}

// Export for type checking in other files
export type { EnvironmentConfig };